import { useEffect, useMemo } from 'react';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { Text } from '@frontend/design-system';
import { findExistingTimePeriodKey } from '../../../../hooks';
import { InfoTipPopover } from '../../../info-tip-popover';
import { nsrTimePeriods, PatientsTypeComparisonText } from '../components';
import { useFetchNSRROITimeline, useNSRRoiShallowStore } from '../hooks';
import { NSRRoiTimelineChartBase } from './nsr-roi-timeline-chart-base';
import { CommonChartProps } from './types';

export const NSRRoiPatientsTypeTimelineChart = ({ onLoading, ...rest }: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters } = useNSRRoiShallowStore('filters');

  const timePeriod =
    filters?.startDate && filters?.endDate
      ? findExistingTimePeriodKey<typeof nsrTimePeriods>(filters.startDate, filters.endDate, nsrTimePeriods) ??
        undefined
      : undefined;

  const nsrRoiTimeline = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.PATIENT_TYPE,
    },
    filters,
  });

  const aggregates = useMemo(() => {
    return (
      nsrRoiTimeline.rawData?.data?.aggregates?.reduce<Record<string, number>>(
        (acc, { name = '', value = 0 }) => ({
          ...acc,
          [name]: value,
        }),
        {}
      ) || {}
    );
  }, [nsrRoiTimeline.rawData?.data?.aggregates]);

  useEffect(() => {
    onLoading?.('patientsTypeChart', nsrRoiTimeline?.isLoading);
  }, [nsrRoiTimeline?.isLoading, onLoading]);

  return (
    <NSRRoiTimelineChartBase
      {...rest}
      {...nsrRoiTimeline}
      beforeChart={
        <PatientsTypeComparisonText
          difference={Math.round((aggregates['New Appt'] || 0) - (aggregates['Follow Up Appt'] || 0))}
          timePeriod={timePeriod}
        />
      }
      infoTip={<InfoTipPopover>{t('Track no-shows based on the  patient type')}</InfoTipPopover>}
      legendValues={{
        newPatients: formatters.appendPercent(aggregates['New Appt'] || 0),
        existingPatients: formatters.appendPercent(aggregates['Follow Up Appt'] || 0),
      }}
      tooltipBottomContentRenderer={(data) => (
        <Text color='subdued' size='small' style={{ maxWidth: '230px' }}>
          <PatientsTypeComparisonText
            difference={(data.newPatients ?? 0) - (data.existingPatients ?? 0)}
            forTooltip
            timePeriod={timePeriod}
          />
        </Text>
      )}
      showLegends
      title={t('No-Show Rate by Patient Type')}
      trackingIdBase='no-show-rate-patients-type'
    />
  );
};
